/**
 * `/tafel/:id?token=…`: the classroom display.
 *
 * Lobby: game name, and for formats with student devices the PIN, a QR code
 * that opens the join page with the PIN filled in, and the names of who
 * joined. Then the module's board view takes the whole screen. At the end,
 * "Beendet". A small fullscreen button fades in on hover.
 *
 * Browsers only allow sound after someone interacted with the page. The
 * lobby shows a chip "Für Ton einmal auf die Tafel klicken" until the first
 * click or key press; then it turns into "Ton ist an".
 */
import type { SessionStatePayload } from '@schulspiele/protocol';

import { createLifetime } from '@shared/lifetime';
import { esc, qs } from '@shared/dom';
import { t } from '@shared/i18n/de';
import { pinDigits, renderChips, setCount } from '@shared/ui/chips';
import { dropLeaves } from '@shared/ui/leaves';
import { armAudioUnlock, audioUnlocked } from '@shared/ui/media-player';
import { qrSvg } from '@shared/ui/qr';
import { withTransition } from '@shared/ui/transition';
import { mountGameView } from '@shared/view-host';
import { SessionClient } from '@shared/ws-client';
import { loadGameClient } from '../games';
import fern from './art/fern-corner.svg?raw';
import { connectionBanner } from './layout';
import { joinUrl } from './teacher/session-page';
import './hub.css';

function screen(content: string): string {
  return `<div class="screen">
    <div class="screen__content">${content}</div>
    <div class="screen__fern screen__fern--left" aria-hidden="true">${fern}</div>
    <div class="screen__fern screen__fern--right" aria-hidden="true">${fern}</div>
  </div>`;
}

export function renderBoard(mount: HTMLElement, sessionId: string, token: string): () => void {
  mount.innerHTML = `
    <div data-banner></div>
    <div class="board-tools"><button class="btn btn--small btn--ghost" type="button" data-fullscreen>${t('board.fullscreen')}</button></div>
    <div class="sound-chip" data-sound role="status"></div>
    <div data-stage>${screen(`<p class="lead">${t('conn.connecting')}</p>`)}</div>`;
  const stage = qs(mount, '[data-stage]');
  const client = new SessionClient({ kind: 'board', sessionId, token });
  let unmountGame: (() => void) | null = null;
  const life = createLifetime();
  let phase: string | null = null;

  const soundChip = qs(mount, '[data-sound]');
  const showSound = (): void => {
    const on = audioUnlocked();
    soundChip.classList.toggle('is-on', on);
    soundChip.textContent = on ? t('board.soundOn') : t('board.soundOff');
    if (on)
      window.setTimeout(() => {
        soundChip.classList.add('is-gone');
      }, 2500);
  };
  const disarm = armAudioUnlock(showSound);
  showSound();

  qs(mount, '[data-fullscreen]').addEventListener('click', () => {
    if (document.fullscreenElement === null)
      void document.documentElement.requestFullscreen().catch(() => undefined);
    else void document.exitFullscreen();
  });

  /** Refused (a wrong board link): only the reason stays, no sound hint or connection note. */
  let refused = false;
  client.on('status', (s) => {
    if (!refused) qs(mount, '[data-banner]').innerHTML = connectionBanner(s);
  });
  // Only a refusal (wrong link, game gone) replaces the stage; other errors
  // (a rate limit, say) end in a reconnect that brings the view back (U19).
  client.on('refused', (e) => {
    refused = true;
    soundChip.hidden = true;
    qs(mount, '[data-banner]').innerHTML = '';
    stage.innerHTML = screen(`<h1>${esc(e.reason ?? t('error.generic'))}</h1>`);
  });
  client.on('state', (s) => {
    void onState(s);
  });
  client.connect();

  async function onState(s: SessionStatePayload): Promise<void> {
    if (s.phase === 'lobby') {
      if (phase !== 'lobby') {
        phase = 'lobby';
        soundChip.hidden = false;
        stage.innerHTML = screen(
          s.needsStudentDevices
            ? `<div class="board-lobby-page" data-lobby>
               <h1>${esc(s.gameName)}</h1>
               <div class="board-lobby">
                 <div class="stack--tight" style="justify-items:center">
                   <p class="lead">${t('session.joinAt')} <strong>${esc(location.host)}/spielen</strong></p>
                   <div class="pin-big" data-pin aria-label="${esc(s.pin ?? '')}">${pinDigits(s.pin ?? '')}</div>
                   <p class="pill pill--bad board-locked" data-locked role="status" hidden>${t('board.locked')}</p>
                 </div>
                 <div class="board-qr" data-qr></div>
               </div>
               <p class="lead" data-count></p>
               <div class="names board-names" data-names></div>
               </div>`
            : `<h1>${esc(s.gameName)}</h1><p class="lead">${t('board.noDevices')}</p>`,
        );
        if (s.needsStudentDevices && s.pin !== undefined) {
          qs(stage, '[data-qr]').innerHTML = qrSvg(joinUrl(s.pin), { label: t('session.qrLabel') });
        }
      }
      if (s.needsStudentDevices) {
        // A locked lobby says so, PIN and QR fade: nobody tries in vain (U16, U19).
        qs(stage, '[data-locked]').hidden = !s.locked;
        qs(stage, '[data-pin]').classList.toggle('is-locked', s.locked);
        qs(stage, '[data-qr]').classList.toggle('is-locked', s.locked);
        const participants = s.participants ?? [];
        // A full class: smaller PIN, QR and names, so all fit on a 720 px board (U19).
        const lobby = qs(stage, '[data-lobby]');
        lobby.classList.toggle('is-crowded', participants.length >= 12);
        lobby.classList.toggle('is-packed', participants.length >= 24);
        // Names arrive at the bottom: the sound hint moves to a corner, out of their way.
        soundChip.classList.toggle('is-corner', participants.length > 0);
        renderChips(qs(stage, '[data-names]'), participants, { markOffline: false });
        setCount(
          qs(stage, '[data-count]'),
          participants.length === 0
            ? t('board.waitingForPlayers')
            : t('session.participants', { count: participants.length }),
        );
      }
      return;
    }
    if (s.phase === 'running' && phase !== 'running') {
      phase = 'running';
      soundChip.hidden = audioUnlocked();
      // During the game the hint moves to a corner, out of the scores' way.
      soundChip.classList.add('is-corner');
      const game = await loadGameClient(s.gameId);
      if (game !== null && !life.isDisposed()) {
        withTransition(() => {
          stage.innerHTML = '';
          unmountGame = mountGameView(game.board, stage, client, 'board', 'board');
        });
      }
      return;
    }
    if (s.phase === 'ended' && phase !== 'ended') {
      phase = 'ended';
      soundChip.hidden = true;
      withTransition(() => {
        unmountGame?.();
        unmountGame = null;
        stage.innerHTML = screen(
          `<h1>${t('session.ended')}</h1><p class="lead">${t('board.thanks')}</p>`,
        );
      });
      dropLeaves(12);
    }
  }

  return () => {
    life.dispose();
    disarm();
    unmountGame?.();
    client.close();
  };
}
